/**
 * Labstep
 *
 * @module components/Layout/FullScreen/Links
 * @desc Labstep Layout
 */

import { Dropdown } from 'labstep-web/core/Dropdown';
import { Icon } from 'labstep-web/core/Icon';
import { NavLink } from 'labstep-web/core/NavLink';
import { PopupContainer as Popup } from 'labstep-web/core/Popup';
import { PremiumFeatureLockIcon } from 'labstep-web/core/PremiumFeature/LockIcon';
import { useActiveGroup } from 'labstep-web/hooks/activeGroup';
import { usePremiumFeatureStatus } from 'labstep-web/hooks/premium-feature';
import { navigation } from 'labstep-web/services/navigation';
import type { IRouting } from 'labstep-web/services/navigation.types';
import React from 'react';
import { matchPath, type NavLinkProps } from 'react-router-dom';
import { extraLinks, subLinks } from './constants';
import styles from './styles.module.scss';
import type { LinksProps } from './types';

export const Links: React.FC<LinksProps> = ({
  desktop,
  group,
  showOverview,
}) => {
  const groupId = group.id;
  const links = usePremiumFeatureStatus(
    Object.keys(subLinks).map((key) => ({
      key,
    })),
    'key',
    'sub_links',
  ).map((link) => (subLinks as any)[link.key]);
  return (
    <div>
      {links
        .filter((link, index) => showOverview || index !== 0)
        .map((link) => {
          const isActive: NavLinkProps['isActive'] = (
            match,
            location,
          ) =>
            link.routes
              ? !!link.routes.find(
                  (route: keyof IRouting) =>
                    !!matchPath(
                      location.pathname,
                      navigation.get(route),
                    ),
                )
              : !!matchPath(
                  location.pathname,
                  navigation.get(link.route.to),
                );
          return (
            <React.Fragment key={link.route.to}>
              {desktop ? (
                <Popup
                  inverted
                  trigger={
                    <NavLink
                      className={styles.iconLink}
                      to={link.route.to}
                      params={{ id: groupId }}
                      isActive={isActive}
                      exact
                    >
                      <Icon name={link.icon} />
                      {link.premiumFeature && (
                        <PremiumFeatureLockIcon
                          premiumFeature={link.premiumFeature}
                        />
                      )}
                    </NavLink>
                  }
                  content={link.text}
                  on="hover"
                  position="right center"
                />
              ) : (
                <NavLink
                  className={styles.link}
                  to={link.route.to}
                  params={{ id: groupId }}
                  isActive={isActive}
                  exact
                >
                  <Icon size="small" name={link.icon} />
                  {link.text}
                  {link.premiumFeature && (
                    <PremiumFeatureLockIcon
                      premiumFeature={link.premiumFeature}
                    />
                  )}
                </NavLink>
              )}

              <div className={styles.border} />
            </React.Fragment>
          );
        })}
      {!!extraLinks.length && (
        <Dropdown
          className={styles.moreLinks}
          pointing="top left"
          trigger={<Icon size="small" name="ellipsis horizontal" />}
          icon={null}
        >
          {extraLinks.map((link: any) => (
            <Dropdown.Item
              key={link.route.to}
              as={NavLink}
              unstyled
              to={link.route.to}
              params={{ id: groupId }}
            >
              <Icon size="small" name={link.icon} />
              {link.text}
            </Dropdown.Item>
          ))}
        </Dropdown>
      )}
    </div>
  );
};

export const LinksActiveGroup: React.FC<Omit<LinksProps, 'group'>> = (
  props,
) => {
  const { activeGroup } = useActiveGroup();
  if (!activeGroup) {
    return null;
  }
  return <Links group={activeGroup} {...props} />;
};
